import { useCallback, useState } from "react";
import { DocumentData } from "../../components/documents/DocumentCollector/types";
import { useCreateDocumentsMutation } from "../../graphql/supergraphHooks";
import { createConsumableMap, consumeFromMap } from "../../utils/consumableMapUtils";
import useUploadFilesToS3 from "../uploadFilesToS3/useUploadFilesToS3";

export default function useDocumentUpload(caseId: number | undefined) {
  const [isUploading, setIsUploading] = useState(false);
  const { uploadFilesToS3, loading: uploadLoading } = useUploadFilesToS3({ caseId });
  const [createDocuments, { loading: createLoading }] = useCreateDocumentsMutation();

  /**
   * Uploads documents to S3 and registers them in the system
   *
   * Process:
   * 1. Extracts files from documents
   * 2. Uploads to S3
   * 3. Registers documents in the system
   * 4. Returns updated documents with assigned IDs
   *
   * @param documents - Documents containing files to upload
   * @returns Updated documents with system-assigned IDs
   */
  const uploadDocuments = useCallback(
    async (documents: DocumentData[]) => {
      // Early return for empty document array
      if (documents.length === 0) {
        return documents;
      }

      // Extract files using map and filter operations
      const filesToUpload = documents
        .map((document) => document.file?.file)
        .filter((file): file is File => file !== undefined);

      // If there are no files to upload, return the original documents
      if (filesToUpload.length === 0 || !caseId) {
        return documents;
      }

      // Now that we know we have files, set loading state
      setIsUploading(true);
      try {
        const uploadResponse = await uploadFilesToS3(filesToUpload);

        const documentInputs = uploadResponse.map((s3File) => ({
          contentType: s3File.file.type,
          fileName: s3File.file.name,
          s3Key: s3File.objectKey || "",
        }));

        const createDocumentsResponse = await createDocuments({
          variables: {
            caseId,
            documents: documentInputs,
          },
        });

        // Update document IDs in files based on the response
        const createdDocuments = createDocumentsResponse.data?.createComplianceDocuments || [];

        // Create a map of filename to array of document IDs (to handle duplicate filenames)
        const createdDocumentsMap = createConsumableMap(
          createdDocuments,
          (doc) => doc.name,
          (doc) => doc.id,
        );

        const updatedDocuments = documents.map((document) => {
          if (!document.file?.file) return document;

          const documentId = consumeFromMap(createdDocumentsMap, document.file.file.name);
          if (documentId) {
            return {
              ...document,
              id: documentId,
            };
          }
          return document;
        });

        return updatedDocuments;
      } finally {
        setIsUploading(false);
      }
    },
    [caseId, createDocuments, uploadFilesToS3],
  );

  return {
    uploadDocuments,
    loading: isUploading || uploadLoading || createLoading,
  };
}
